import { POLL_OPTION_MAX_LENGTH, visibilityTypeValues, } from '../types/index.js';
import { canModerateJam } from './jamHelpers.js';
import { defaultRsvpRecurrenceId, listRsvpOccurrences, normalizeRecurrenceId, sameRecurrenceId, } from './eventRecurrence.js';
import { checkGroupCapabilities, checkPostCapabilities, checkRoleCapabilities, } from './capabilitiesHelpers.js';
import { scopeMatches } from './scopeHelpers.js';
import { countBy, dateSorter, groupBy, transformValues } from './utils.js';
const buildThread = (rootPost, postList) => ({
    ...rootPost,
    children: postList
        .filter((p) => p.inReplyToId === rootPost.id)
        .map((post) => buildThread(post, postList)),
});
export const buildThreads = (posts) => posts
    .filter((p) => !posts.find((p2) => p2.id === p.inReplyToId))
    .map((post) => buildThread(post, posts))
    .sort(dateSorter());
const parentIdOf = (post) => { var _a, _b; return (_a = post.inReplyToId) !== null && _a !== void 0 ? _a : (_b = post.replyTo) === null || _b === void 0 ? void 0 : _b.id; };
export const buildThreadPreview = (rootId, replies) => {
    var _a;
    const sorted = [...replies].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
    if (!sorted.length)
        return [];
    const ids = new Set(sorted.map((post) => post.id));
    const parent = new Map(sorted.map((post) => [post.id, post.id]));
    const find = (id) => {
        var _a;
        const current = (_a = parent.get(id)) !== null && _a !== void 0 ? _a : id;
        if (current !== id) {
            const root = find(current);
            parent.set(id, root);
            return root;
        }
        return id;
    };
    const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb)
            parent.set(ra, rb);
    };
    for (const post of sorted) {
        const parentId = parentIdOf(post);
        if (parentId && ids.has(parentId))
            union(post.id, parentId);
    }
    const grouped = new Map();
    for (const post of sorted) {
        const key = find(post.id);
        const list = (_a = grouped.get(key)) !== null && _a !== void 0 ? _a : [];
        list.push(post);
        grouped.set(key, list);
    }
    return [...grouped.values()]
        .map((posts) => {
        var _a, _b;
        const start = (_a = posts.find((post) => {
            const parentId = parentIdOf(post);
            return !parentId || !ids.has(parentId);
        })) !== null && _a !== void 0 ? _a : posts[0];
        const ordered = [];
        const used = new Set();
        let current = start;
        while (current && !used.has(current.id)) {
            ordered.push(current);
            used.add(current.id);
            current = posts.find((post) => parentIdOf(post) === (current === null || current === void 0 ? void 0 : current.id) && !used.has(post.id));
        }
        for (const post of posts) {
            if (!used.has(post.id))
                ordered.push(post);
        }
        const startParentId = parentIdOf(start);
        const ancestor = startParentId && startParentId !== rootId
            ? ((_b = start.replyTo) !== null && _b !== void 0 ? _b : undefined)
            : undefined;
        const skippedAncestor = !!(ancestor &&
            ancestor.inReplyToId &&
            ancestor.inReplyToId !== rootId);
        return { skippedAncestor, ancestor, posts: ordered };
    })
        .sort((a, b) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const aKey = (_d = (_b = (_a = a.ancestor) === null || _a === void 0 ? void 0 : _a.createdAt) !== null && _b !== void 0 ? _b : (_c = a.posts[0]) === null || _c === void 0 ? void 0 : _c.createdAt) !== null && _d !== void 0 ? _d : '';
        const bKey = (_h = (_f = (_e = b.ancestor) === null || _e === void 0 ? void 0 : _e.createdAt) !== null && _f !== void 0 ? _f : (_g = b.posts[0]) === null || _g === void 0 ? void 0 : _g.createdAt) !== null && _h !== void 0 ? _h : '';
        return aKey.localeCompare(bKey);
    });
};
export const getReactionCount = (post) => {
    return countBy(post.reactions, (r) => r.reaction);
};
export const countVotes = (optionsCount, answers) => {
    const voteCounts = new Array(optionsCount).fill(0);
    for (const answer of answers) {
        for (const index of answer.selection) {
            if (index < optionsCount) {
                voteCounts[index] = voteCounts[index] + 1;
            }
        }
    }
    return voteCounts;
};
/** Fill blank poll choices with their placeholder label (e.g. "Option 1"). */
export const resolvePollOptionContents = (options, fallbackLabel) => options.map((text, index) => {
    const trimmed = text.trim();
    return trimmed.length > 0 ? trimmed : fallbackLabel(index);
});
/** Longer choices are rejected by `questionSchema`, so block submit instead. */
export const pollOptionsWithinLimit = (options) => options.every((text) => text.trim().length <= POLL_OPTION_MAX_LENGTH);
export const collectVotes = (post) => {
    if (post.type !== 'question') {
        return {
            voteCounts: [],
            votes: [],
        };
    }
    const postData = post.data;
    const sortedVoteEntries = post.entries
        .filter((e) => e.type === 'answer')
        .sort(dateSorter());
    const votesByProfile = groupBy(sortedVoteEntries, (e) => e.profile.id);
    const lastVoteByProfile = transformValues(votesByProfile, (votes) => votes.slice(-1)[0]);
    const votes = [...Object.values(lastVoteByProfile)]
        .filter((ve) => ve.data.selection.length > 0)
        .map((entry) => ({
        profile: entry.profile,
        selection: entry.data.selection,
    }));
    const voteCounts = countVotes(postData.options.length, votes);
    return {
        votes,
        voteCounts,
    };
};
const latestRsvpPerProfile = (rsvps, recurrenceId) => {
    const scoped = rsvps.filter((rsvp) => {
        if (recurrenceId) {
            if (rsvp.recurrenceId) {
                return sameRecurrenceId(rsvp.recurrenceId, recurrenceId);
            }
            return true;
        }
        return true;
    });
    const rsvpsByProfile = groupBy(scoped, (r) => r.profile.id);
    return Object.values(rsvpsByProfile).map((profileRsvps) => {
        const instanceRsvps = recurrenceId
            ? profileRsvps.filter((rsvp) => sameRecurrenceId(rsvp.recurrenceId, recurrenceId))
            : [];
        const seriesRsvps = recurrenceId
            ? profileRsvps.filter((rsvp) => !rsvp.recurrenceId)
            : profileRsvps;
        const pool = recurrenceId && instanceRsvps.length > 0 ? instanceRsvps : seriesRsvps;
        const sorted = pool.sort(dateSorter());
        return sorted[sorted.length - 1];
    });
};
export const calculateEffectiveRsvps = (post, recurrenceId) => latestRsvpPerProfile(post.rsvps || [], recurrenceId).filter((rsvp) => !!rsvp);
export const getEffectiveRsvp = (post, profileId, recurrenceId) => calculateEffectiveRsvps(post, recurrenceId).find((r) => r.profile.id === profileId);
export const countYesRsvps = (post, recurrenceId) => calculateEffectiveRsvps(post, recurrenceId).filter((r) => r.response === 'yes').length;
export const countSeriesYesRsvps = (post) => {
    var _a;
    return ((_a = post.rsvps) !== null && _a !== void 0 ? _a : []).filter((r) => !r.recurrenceId && r.response === 'yes')
        .length;
};
export const instanceRsvpIdsForProfile = (post, profileId) => {
    var _a;
    const ids = new Set();
    for (const rsvp of (_a = post.rsvps) !== null && _a !== void 0 ? _a : []) {
        if (rsvp.profile.id !== profileId || !rsvp.recurrenceId)
            continue;
        ids.add(normalizeRecurrenceId(rsvp.recurrenceId));
    }
    return [...ids];
};
export const overlaySeriesRsvpEntries = (response, instanceIds) => [
    { response },
    ...instanceIds.map((recurrenceId) => ({ response, recurrenceId })),
];
export const seriesYesBlockedByCapacity = (post, profileId, now = new Date()) => {
    var _a;
    const event = ((_a = post.data) === null || _a === void 0 ? void 0 : _a.type) === 'event' ? post.data : undefined;
    const maxAttendees = event === null || event === void 0 ? void 0 : event.maxAttendees;
    if (!event || !maxAttendees || !event.recurrence)
        return false;
    const occurrences = listRsvpOccurrences(event, now);
    // When there are no upcoming occurrences (e.g. a live jam whose current
    // occurrence started before `now`), fall back to the series-level count.
    if (occurrences.length === 0) {
        const current = getEffectiveRsvp(post, profileId);
        if ((current === null || current === void 0 ? void 0 : current.response) !== 'yes' &&
            countSeriesYesRsvps(post) >= maxAttendees) {
            return true;
        }
        return false;
    }
    return occurrences.some((occurrence) => {
        const current = getEffectiveRsvp(post, profileId, occurrence.recurrenceId);
        if ((current === null || current === void 0 ? void 0 : current.response) === 'yes')
            return false;
        return countYesRsvps(post, occurrence.recurrenceId) >= maxAttendees;
    });
};
export const occurrenceHasCapacity = (post, recurrenceId, profileId) => {
    var _a;
    const event = ((_a = post.data) === null || _a === void 0 ? void 0 : _a.type) === 'event' ? post.data : undefined;
    if (!(event === null || event === void 0 ? void 0 : event.maxAttendees))
        return true;
    if (profileId) {
        const current = getEffectiveRsvp(post, profileId, recurrenceId);
        if ((current === null || current === void 0 ? void 0 : current.response) === 'yes')
            return true;
    }
    return countYesRsvps(post, recurrenceId) < event.maxAttendees;
};
/** Series Register stays enabled while any upcoming date still has a seat. */
export const recurringEventHasOpenOccurrence = (post, profileId, now = new Date()) => {
    var _a;
    const event = ((_a = post.data) === null || _a === void 0 ? void 0 : _a.type) === 'event' ? post.data : undefined;
    if (!event)
        return false;
    if (!event.recurrence) {
        return occurrenceHasCapacity(post, undefined, profileId);
    }
    return listRsvpOccurrences(event, now).some((occurrence) => occurrenceHasCapacity(post, occurrence.recurrenceId, profileId));
};
/**
 * Series-page RSVP for the Register/Cancel CTA: prefer the default/next date,
 * then any attending instance so a partial RSVP still shows Cancel.
 */
export const displayRsvpForProfile = (post, profileId, options) => {
    var _a;
    const event = ((_a = post.data) === null || _a === void 0 ? void 0 : _a.type) === 'event' ? post.data : undefined;
    const scopedId = (options === null || options === void 0 ? void 0 : options.lockToOccurrence)
        ? options.recurrenceId
        : event
            ? defaultRsvpRecurrenceId(event, options === null || options === void 0 ? void 0 : options.recurrenceId, options === null || options === void 0 ? void 0 : options.now)
            : options === null || options === void 0 ? void 0 : options.recurrenceId;
    const scoped = getEffectiveRsvp(post, profileId, scopedId);
    if (options === null || options === void 0 ? void 0 : options.lockToOccurrence)
        return scoped;
    if (scoped && scoped.response !== 'no')
        return scoped;
    if (event === null || event === void 0 ? void 0 : event.recurrence) {
        for (const occurrence of listRsvpOccurrences(event, options === null || options === void 0 ? void 0 : options.now)) {
            const instance = getEffectiveRsvp(post, profileId, occurrence.recurrenceId);
            if (instance && instance.response !== 'no')
                return instance;
        }
    }
    return scoped;
};
export const isCapacityEvent = (event) => !!event.maxAttendees;
export const parseEventMaxAttendeesInput = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};
export const withoutEventMaxAttendees = (event) => {
    const { maxAttendees: _, ...rest } = event;
    return rest;
};
export const normalizeEventDataForSave = (event) => {
    const { maxAttendees } = event;
    if (maxAttendees == null ||
        !Number.isFinite(maxAttendees) ||
        maxAttendees < 1) {
        return withoutEventMaxAttendees(event);
    }
    return { ...event, maxAttendees: Math.trunc(maxAttendees) };
};
/** Null capacity means it was cleared; strip for validation/output. */
export const normalizeEventDataFromDb = (event) => {
    if (event.maxAttendees != null) {
        return event;
    }
    return withoutEventMaxAttendees(event);
};
export const normalizePostDataFromDb = (data) => data.type === 'event' ? normalizeEventDataFromDb(data) : data;
export const eventDataForDbUpdate = (previous, normalized) => {
    if ((previous === null || previous === void 0 ? void 0 : previous.maxAttendees) != null && !('maxAttendees' in normalized)) {
        return { ...normalized, maxAttendees: null };
    }
    return normalized;
};
export const canManageEventRsvps = (profile, post) => !!(profile &&
    (post.profile.id === profile.id || canModerateJam(profile, post)));
export const getJamCapacityJoinBlock = (post, profile, recurrenceId) => {
    var _a;
    const event = ((_a = post.data) === null || _a === void 0 ? void 0 : _a.type) === 'event' ? post.data : undefined;
    if (!(event === null || event === void 0 ? void 0 : event.maxAttendees) || !(profile === null || profile === void 0 ? void 0 : profile.id)) {
        return { blocked: false };
    }
    if (canModerateJam(profile, post)) {
        return { blocked: false };
    }
    const myRsvp = getEffectiveRsvp(post, profile.id, recurrenceId);
    if ((myRsvp === null || myRsvp === void 0 ? void 0 : myRsvp.response) === 'yes') {
        return { blocked: false };
    }
    if ((myRsvp === null || myRsvp === void 0 ? void 0 : myRsvp.response) === 'removed') {
        return { blocked: true, reason: 'removed' };
    }
    if (countYesRsvps(post, recurrenceId) >= event.maxAttendees) {
        return { blocked: true, reason: 'full' };
    }
    return { blocked: true, reason: 'rsvp-required' };
};
export const canDeletePost = (authData, post, config) => checkPostCapabilities(authData, ['core-posts-delete'], post, config).success;
export const canCreatePost = (authData, type, visibility, group) => {
    var _a;
    return visibility === 'group'
        ? !!group &&
            checkGroupCapabilities(authData, [`core-posts-create-${type}`], group)
                .success
        : checkRoleCapabilities((_a = authData.profile) === null || _a === void 0 ? void 0 : _a.roles, [
            `core-posts-create-${type}-${visibility}`,
        ]).success &&
            scopeMatches({
                scopes: authData.scopes,
                requiredScope: { scopeLevel: 'write', resource: { type: 'posts' } },
            });
};
export const canCreatePostTypeInAnyGroup = (authData, type) => {
    var _a, _b;
    return scopeMatches({
        scopes: authData.scopes,
        requiredScope: { scopeLevel: 'write', resource: { type: 'groups' } },
    }) &&
        !!((_b = (_a = authData.profile) === null || _a === void 0 ? void 0 : _a.memberships) === null || _b === void 0 ? void 0 : _b.some((m) => checkGroupCapabilities(authData, [`core-posts-create-${type}`], m.group).success));
};
export const canCreatePostTypeWithVisibility = (authData, type, visibility) => {
    var _a;
    return visibility === 'group'
        ? canCreatePostTypeInAnyGroup(authData, type)
        : checkRoleCapabilities((_a = authData.profile) === null || _a === void 0 ? void 0 : _a.roles, [
            `core-posts-create-${type}-${visibility}`,
        ]).success &&
            scopeMatches({
                scopes: authData.scopes,
                requiredScope: { scopeLevel: 'write', resource: { type: 'posts' } },
            });
};
export const canCreatePostType = (authData, type) => visibilityTypeValues.some((visibility) => canCreatePostTypeWithVisibility(authData, type, visibility));
/**
 * Whether Reply / Repost / Like should look live. Non-members of a group
 * post stay fully disabled (existing feed rule). Each action also follows
 * the capability the matching write endpoint actually checks: feed replies
 * and reposts need create-*, likes need core-posts-react.
 */
export const getPostActionAvailability = (authData, post, config) => {
    var _a, _b, _c;
    const isGroupPost = !!post.groupId || !!post.group;
    const membershipExists = !!((_b = (_a = authData.profile) === null || _a === void 0 ? void 0 : _a.memberships) === null || _b === void 0 ? void 0 : _b.some((m) => { var _a; return m.group.id === ((_a = post.group) === null || _a === void 0 ? void 0 : _a.id) || m.group.id === post.groupId; }));
    if (isGroupPost && !membershipExists) {
        return { canReply: false, canRepost: false, canReact: false };
    }
    const canReply = checkPostCapabilities(authData, ['core-posts-reply'], post, config)
        .success &&
        canCreatePost(authData, 'note', post.visibility, (_c = post.group) !== null && _c !== void 0 ? _c : undefined);
    const canReact = checkPostCapabilities(authData, ['core-posts-react'], post, config).success;
    const canRepost = post.group
        ? checkGroupCapabilities(authData, [`core-posts-create-${post.type}`], post.group).success
        : canCreatePost(authData, post.type, post.visibility);
    return { canReply, canRepost, canReact };
};
/** Sentinel handle for block-hidden reply tombstones (no name/handle leak). */
export const HIDDEN_AUTHOR_HANDLE = 'hidden';
export const toHiddenPost = (post) => ({
    ...post,
    hidden: true,
    data: {
        ...post.data,
        ...('content' in post.data ? { content: '' } : {}),
    },
    profile: {
        ...post.profile,
        displayName: undefined,
        handle: HIDDEN_AUTHOR_HANDLE,
        avatar: null,
        header: null,
        bio: undefined,
    },
});
export const isHiddenPost = (post) => !!post.hidden;
const participantIds = (post) => {
    var _a, _b, _c;
    const ids = new Set(((_a = post.audience) !== null && _a !== void 0 ? _a : []).map((member) => member.id).filter(Boolean));
    const creatorId = (_b = post.creatorId) !== null && _b !== void 0 ? _b : (_c = post.profile) === null || _c === void 0 ? void 0 : _c.id;
    if (creatorId)
        ids.add(creatorId);
    return ids;
};
export const isOneToOneWithBlocked = (post, viewer, blockedIds) => {
    if (!viewer || post.visibility !== 'direct')
        return false;
    const blocked = blockedIds instanceof Set ? blockedIds : new Set(blockedIds !== null && blockedIds !== void 0 ? blockedIds : []);
    if (!blocked.size)
        return false;
    const others = participantIds(post);
    others.delete(viewer.id);
    return others.size === 1 && blocked.has([...others][0]);
};
